// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCartLinesHeader from "@hardwoods/App/Components/Cart/XngCartLinesHeader";
import CartLineCardExpanded, {
    CartLineCardExpandedStyles,
} from "@hardwoods/Overrides/Widgets/Cart/CartLineCardExpanded";
import { cartLinesGroupingHandler, cartLinesHeaderGroup } from "@hardwoods/Overrides/Widgets/Cart/helper";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { AvailabilityMessageType, CartLineModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import CartLineCardCondensed, {
    CartLineCardCondensedStyles,
} from "@insite/content-library/Components/CartLineCardCondensed";
import CartLinesListHeader, { CartLinesListHeaderStyles } from "@insite/content-library/Components/CartLinesListHeader";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    isCondensed: boolean;
    hideCondensedSelector?: boolean;
    onChangeIsCondensed?: (event: React.SyntheticEvent<Element, Event>, value: boolean) => void;
    editable?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLinesListStyles;
}

export interface CartLinesListStyles {
    wrapper?: InjectableCss;
    header?: CartLinesListHeaderStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    pagination?: PaginationPresentationProps;
}

export const orderLinesListStyles: CartLinesListStyles = {
    wrapper: {
        css: css`
            @media print {
                break-inside: avoid;
            }
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
};

const CartLinesList: FC<OwnProps> = ({
    cart,
    promotions,
    isCondensed,
    hideCondensedSelector,
    onChangeIsCondensed,
    editable,
    showSavingsAmount,
    showSavingsPercent,
    extendedStyles,
    hideAddToList,
}) => {
    const [styles] = React.useState(() => mergeWithCss(orderLinesListStyles, extendedStyles));
    const productSettings = useAppSelector(state => getSettingsCollection(state).productSettings);

    if (!cart.cartLines) {
        return null;
    }

    const cartLines =
        cart.status === "Cart" ? cart.cartLines.filter(cartline => !cartline.quoteRequired) : cart.cartLines;

    // const CartLineComponent = isCondensed ? CartLineCardCondensed : CartLineCardExpanded;

    // XNG-Changes
    const buildCartLines = (cartLines: CartLineModel[]) =>
        cartLines?.map(cartLine => {
            const cartLinePromotions = promotions?.filter(promotion => promotion.orderLineId === cartLine.id);
            return (
                <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                    <CartLineCardExpanded
                        cart={cart}
                        promotions={cartLinePromotions ?? []}
                        productSettings={productSettings}
                        showSavingsAmount={showSavingsAmount}
                        showSavingsPercent={showSavingsPercent}
                        lineQuantityEditable={editable}
                        lineNotesEditable={editable}
                        hideAddToList={hideAddToList}
                        showLineNotes={true}
                        showRemoveAction={false}
                        showInventoryAvailability={true}
                        extendedStyles={styles.cardExpanded}
                    />
                </CartLineContext.Provider>
            );
        });

    const cartLinesDisplay: JSX.Element[] = [];
    const { cartLinesAvailabilityGroup, cartLinesOtherGroup } = cartLinesGroupingHandler(cartLines);

    if (cartLinesAvailabilityGroup.length > 0) {
        cartLinesDisplay.push(<XngCartLinesHeader leftHeaderTitle={cartLinesHeaderGroup.available} />);
        cartLinesDisplay.push(...buildCartLines(cartLinesAvailabilityGroup));
        cartLinesDisplay.push(<StyledWrapper style={{ marginBottom: cartLinesOtherGroup.length > 0 ? 30 : 0 }} />);
    }
    if (cartLinesOtherGroup.length > 0) {
        cartLinesDisplay.push(<XngCartLinesHeader leftHeaderTitle={cartLinesHeaderGroup.otherAvailability} />);
        cartLinesDisplay.push(...buildCartLines(cartLinesOtherGroup));
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`cardLinesList_${cart.id}`}>
            <CartLinesListHeader
                productsCount={cartLines.length}
                isCondensed={isCondensed}
                hideCondensedSelector={true}
                onChangeIsCondensed={onChangeIsCondensed}
                extendedStyles={styles.header}
            />
            {/* XNG-Changes: CartLines Group */}
            {cartLinesDisplay}
        </StyledWrapper>
    );
};

CartLinesList.defaultProps = {
    hideAddToList: true,
};

export default CartLinesList;
